import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { getDepartments } from '../../../services/organizationStructureService';
import {
  createPlannedDemand,
  deactivatePlannedDemand,
  listPlannedDemand,
} from '../../../services/plannedDemandService';
import { buildCapacityOutlook } from '../../../components/ops/capacityBalance';
import {
  capacityFieldSourceLabel,
  resolveCapacityParameters,
} from '../../../components/ops/capacityParameters';
import {
  createCapacityParameter,
  deactivateCapacityParameter,
  listCapacityParameters,
} from '../../../services/capacityParameterService';
import {
  demandSourceLabel,
  demandUnitLabel,
  localDateIso,
  resolvePlannedDemand,
} from '../../../components/ops/plannedDemand';
import { ErrorState } from '../../shared/components/ErrorState';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';

export const PlannedDemandPage: React.FC = () => {
  const { workspace } = useWorkspace();
  const organizationId = workspace?.organizationId ?? '';
  const queryClient = useQueryClient();
  const today = localDateIso();
  const [scopeId, setScopeId] = useState('organization');
  const [value, setValue] = useState('5');
  const [validFrom, setValidFrom] = useState(today);
  const [validTo, setValidTo] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const departmentsQuery = useQuery({
    queryKey: ['planned-demand-departments', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => getDepartments(organizationId),
  });

  const demandQuery = useQuery({
    queryKey: ['planned-demand', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => listPlannedDemand(organizationId),
  });

  const saveMutation = useMutation({
    mutationFn: createPlannedDemand,
    onSuccess: async () => {
      setFormError(null);
      await queryClient.invalidateQueries({ queryKey: ['planned-demand', organizationId] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivatePlannedDemand,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['planned-demand', organizationId] });
    },
  });

  const previewDepartmentId = scopeId === 'organization' ? null : scopeId;
  const resolved = useMemo(
    () => resolvePlannedDemand(demandQuery.data ?? [], { departmentId: previewDepartmentId }, today),
    [demandQuery.data, previewDepartmentId, today],
  );

  const save = () => {
    const parsed = Number(value.replace(',', '.'));
    if (!organizationId) return;
    if (!Number.isFinite(parsed) || parsed < 0) {
      setFormError('Informe uma demanda maior ou igual a zero.');
      return;
    }
    if (!validFrom) {
      setFormError('Informe a data inicial da vigência.');
      return;
    }
    if (validTo && validTo < validFrom) {
      setFormError('A data final não pode ser anterior à data inicial.');
      return;
    }
    setFormError(null);
    saveMutation.mutate({
      organizationId,
      departmentId: previewDepartmentId,
      value: parsed,
      unit: 'tasks_per_hour',
      validFrom,
      validTo: validTo || null,
    });
  };

  return (
    <div className="enterprise-page h-full overflow-y-auto">
      <div className="space-y-[var(--block-gap)]">
        <ModuleCard title="Demanda planejada">
          <p className="mb-4 text-xs enterprise-text-secondary">
            Utilizada para análise de capacidade. Não representa WIP nem throughput histórico.
            A unidade desta versão é {demandUnitLabel('tasks_per_hour')}. Tempo padrão por tarefa não é preenchido.
          </p>

          {departmentsQuery.isPending || demandQuery.isPending ? (
            <ModuleLoadingState label="Carregando demanda planejada" />
          ) : null}

          {demandQuery.isError ? (
            <ErrorState
              message="Não foi possível ler a demanda planejada. A estrutura ainda pode não ter sido aplicada no banco."
              onRetry={demandQuery.refetch}
            />
          ) : null}

          {!demandQuery.isPending && !demandQuery.isError && (
            <>
              <div className="mb-4 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Vigente hoje neste escopo</p>
                {resolved ? (
                  <>
                    <p className="mt-1 text-sm font-semibold tabular-nums text-[var(--text-primary)]">
                      {resolved.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} {demandUnitLabel(resolved.unit)}
                    </p>
                    <p className="text-[11px] text-[var(--text-muted)]">{demandSourceLabel(resolved.source)}</p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-[var(--text-muted)]">— Não configurada</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
                  Escopo
                  <select
                    value={scopeId}
                    onChange={(event) => setScopeId(event.target.value)}
                    className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px] text-[var(--text-primary)]"
                  >
                    <option value="organization">Organização</option>
                    {(departmentsQuery.data ?? []).map((department) => (
                      <option key={department.id} value={department.id}>{department.name}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
                  Demanda ({demandUnitLabel('tasks_per_hour')})
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px] text-[var(--text-primary)]"
                  />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
                  Vigência inicial
                  <input
                    type="date"
                    value={validFrom}
                    onChange={(event) => setValidFrom(event.target.value)}
                    className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px] text-[var(--text-primary)]"
                  />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
                  Vigência final (opcional)
                  <input
                    type="date"
                    value={validTo}
                    onChange={(event) => setValidTo(event.target.value)}
                    className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px] text-[var(--text-primary)]"
                  />
                </label>
              </div>

              {formError ? <p className="mt-2 text-[11px] text-red-600">{formError}</p> : null}
              {saveMutation.isError ? (
                <p className="mt-2 text-[11px] text-red-600">Não foi possível gravar. Confira se a migration de demanda planejada já foi aplicada.</p>
              ) : null}

              <button
                type="button"
                onClick={save}
                disabled={saveMutation.isPending}
                className="enterprise-button-secondary mt-3 px-4 py-2 text-sm font-medium"
              >
                {saveMutation.isPending ? 'Salvando...' : 'Salvar demanda planejada'}
              </button>

              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-left text-[11px]">
                  <thead>
                    <tr className="border-b border-[var(--border-default)] text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                      <th className="py-2 pr-2">Escopo</th>
                      <th className="py-2 pr-2">Demanda</th>
                      <th className="py-2 pr-2">Vigência</th>
                      <th className="py-2 pr-2">Situação</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {(demandQuery.data ?? []).map((record) => {
                      const department = (departmentsQuery.data ?? []).find((item) => item.id === record.departmentId);
                      return (
                        <tr key={record.id} className="border-b border-[var(--border-default)]/60">
                          <td className="py-2 pr-2">{record.departmentId ? (department?.name ?? 'Departamento') : 'Organização'}</td>
                          <td className="py-2 pr-2 tabular-nums">
                            {record.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} {demandUnitLabel(record.unit)}
                          </td>
                          <td className="py-2 pr-2">{record.validFrom}{record.validTo ? ` → ${record.validTo}` : ''}</td>
                          <td className="py-2 pr-2">{record.active ? 'Ativa' : 'Inativa'}</td>
                          <td className="py-2 text-right">
                            {record.active ? (
                              <button
                                type="button"
                                onClick={() => deactivateMutation.mutate(record.id)}
                                className="text-[11px] font-semibold text-[var(--text-secondary)]"
                              >
                                Desativar
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(demandQuery.data ?? []).length === 0 ? (
                  <p className="py-3 text-[11px] text-[var(--text-muted)]">Nenhuma demanda planejada gravada.</p>
                ) : null}
              </div>
            </>
          )}
        </ModuleCard>
        <CapacitySettingsCard
          organizationId={organizationId}
          scopeId={scopeId}
          departments={departmentsQuery.data ?? []}
          demandRecords={demandQuery.data ?? []}
          today={today}
        />
      </div>
    </div>
  );
};

function fmt(value: number | null, digits = 1): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

const CapacitySettingsCard: React.FC<{
  organizationId: string;
  scopeId: string;
  departments: { id: string; name: string }[];
  demandRecords: Parameters<typeof resolvePlannedDemand>[0];
  today: string;
}> = ({ organizationId, scopeId, departments, demandRecords, today }) => {
  const queryClient = useQueryClient();
  const [fte, setFte] = useState('');
  const [productivity, setProductivity] = useState('');
  const [validFrom, setValidFrom] = useState(today);
  const [validTo, setValidTo] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const departmentId = scopeId === 'organization' ? null : scopeId;

  const capacityQuery = useQuery({
    queryKey: ['capacity-parameter', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => listCapacityParameters(organizationId),
  });

  const saveMutation = useMutation({
    mutationFn: createCapacityParameter,
    onSuccess: async () => {
      setFormError(null);
      await queryClient.invalidateQueries({ queryKey: ['capacity-parameter', organizationId] });
    },
  });
  const deactivateMutation = useMutation({
    mutationFn: deactivateCapacityParameter,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['capacity-parameter', organizationId] });
    },
  });

  const resolvedCapacity = useMemo(
    () => resolveCapacityParameters(capacityQuery.data ?? [], { departmentId }, today),
    [capacityQuery.data, departmentId, today],
  );
  const resolvedDemand = useMemo(
    () => resolvePlannedDemand(demandRecords, { departmentId }, today),
    [demandRecords, departmentId, today],
  );
  const outlook = buildCapacityOutlook({
    demandTasksPerHour: resolvedDemand?.value ?? null,
    demandOrigin: resolvedDemand?.source ?? null,
    availableFte: resolvedCapacity.availableFte?.value ?? null,
    tasksPerPersonHour: resolvedCapacity.productivityTasksPerPersonHour?.value ?? null,
  });

  const save = () => {
    const parsedFte = fte.trim() === '' ? null : Number(fte.replace(',', '.'));
    const parsedProductivity = productivity.trim() === '' ? null : Number(productivity.replace(',', '.'));
    if (parsedFte == null && parsedProductivity == null) {
      setFormError('Informe FTE, produtividade ou os dois. Campo vazio herda da organização.');
      return;
    }
    if (parsedFte != null && (!Number.isFinite(parsedFte) || parsedFte < 0)) {
      setFormError('FTE disponível precisa ser zero ou maior.');
      return;
    }
    if (parsedProductivity != null && (!Number.isFinite(parsedProductivity) || parsedProductivity <= 0)) {
      setFormError('Produtividade precisa ser maior que zero. Zero não entra na divisão do FTE necessário.');
      return;
    }
    if (!validFrom || (validTo && validTo < validFrom)) {
      setFormError('Confira a vigência.');
      return;
    }
    setFormError(null);
    saveMutation.mutate({
      organizationId,
      departmentId,
      availableFte: parsedFte,
      productivityTasksPerPersonHour: parsedProductivity,
      validFrom,
      validTo: validTo || null,
    });
  };

  return (
    <ModuleCard title="Capacidade e balanceamento">
      <p className="mb-3 text-xs enterprise-text-secondary">
        Capacidade = FTE disponível × produtividade. FTE necessário = demanda ÷ produtividade.
        Pessoas cadastradas não substituem o FTE. Produtividade zero não é aceita.
      </p>
      {capacityQuery.isError ? (
        <ErrorState message="Não foi possível ler a capacidade. A migration de capacity_parameter pode ainda não ter sido aplicada." onRetry={capacityQuery.refetch} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-[4px] border border-[var(--border-default)] px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Demanda</p>
              <p className="mt-1 text-sm font-semibold tabular-nums">{fmt(outlook.demandTasksPerHour)} {outlook.demandTasksPerHour == null ? '' : demandUnitLabel('tasks_per_hour')}</p>
              <p className="text-[11px] text-[var(--text-muted)]">{resolvedDemand ? demandSourceLabel(resolvedDemand.source) : 'Não configurada'}</p>
            </div>
            <div className="rounded-[4px] border border-[var(--border-default)] px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Capacidade</p>
              <p className="mt-1 text-[12px]">FTE disponível: <strong className="tabular-nums">{fmt(outlook.availableFte)}</strong></p>
              <p className="text-[11px] text-[var(--text-muted)]">{resolvedCapacity.availableFte ? capacityFieldSourceLabel(resolvedCapacity.availableFte.source) : 'Não configurado'}</p>
              <p className="mt-1 text-[12px]">Produtividade: <strong className="tabular-nums">{fmt(outlook.tasksPerPersonHour)}</strong> tarefa/pessoa/h</p>
              <p className="text-[11px] text-[var(--text-muted)]">{resolvedCapacity.productivityTasksPerPersonHour ? capacityFieldSourceLabel(resolvedCapacity.productivityTasksPerPersonHour.source) : 'Não configurada'}</p>
              <p className="mt-1 text-[12px]">Capacidade calculada: <strong className="tabular-nums">{fmt(outlook.capacityTasksPerHour)}</strong> tarefas/h</p>
            </div>
            <div className="rounded-[4px] border border-[var(--border-default)] px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Balanceamento</p>
              <p className="mt-1 text-[12px]">Gap: <strong className="tabular-nums">{fmt(outlook.gapTasksPerHour)}</strong> tarefas/h</p>
              <p className="text-[12px]">FTE necessário: <strong className="tabular-nums">{fmt(outlook.requiredFte, 3)}</strong></p>
              <p className="text-[12px]">Déficit de FTE: <strong className="tabular-nums">{fmt(outlook.fteDeficit, 3)}</strong></p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
              FTE disponível
              <input value={fte} onChange={(event) => setFte(event.target.value)} placeholder="vazio = herdar" className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px]" />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
              Produtividade (tarefas/pessoa/h)
              <input value={productivity} onChange={(event) => setProductivity(event.target.value)} placeholder="vazio = herdar" className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px]" />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
              Vigência inicial
              <input type="date" value={validFrom} onChange={(event) => setValidFrom(event.target.value)} className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px]" />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-[var(--text-secondary)]">
              Vigência final (opcional)
              <input type="date" value={validTo} onChange={(event) => setValidTo(event.target.value)} className="rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[12px]" />
            </label>
          </div>
          <p className="mt-2 text-[11px] text-[var(--text-muted)]">O escopo é o mesmo da demanda, acima. Um campo vazio não apaga o outro: o departamento herda só o que não preencher.</p>
          {formError ? <p className="mt-2 text-[11px] text-red-600">{formError}</p> : null}
          <button type="button" onClick={save} disabled={saveMutation.isPending} className="enterprise-button-secondary mt-3 px-4 py-2 text-sm font-medium">
            {saveMutation.isPending ? 'Salvando...' : 'Salvar capacidade'}
          </button>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead>
                <tr className="border-b border-[var(--border-default)] text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="py-2 pr-2">Escopo</th>
                  <th className="py-2 pr-2">FTE</th>
                  <th className="py-2 pr-2">Produtividade</th>
                  <th className="py-2 pr-2">Vigência</th>
                  <th className="py-2 pr-2">Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(capacityQuery.data ?? []).map((record) => (
                  <tr key={record.id} className="border-b border-[var(--border-default)]/60">
                    <td className="py-2 pr-2">{record.departmentId ? (departments.find((item) => item.id === record.departmentId)?.name ?? 'Departamento') : 'Organização'}</td>
                    <td className="py-2 pr-2 tabular-nums">{record.availableFte == null ? '—' : fmt(record.availableFte)}</td>
                    <td className="py-2 pr-2 tabular-nums">{record.productivityTasksPerPersonHour == null ? '—' : fmt(record.productivityTasksPerPersonHour)}</td>
                    <td className="py-2 pr-2">{record.validFrom}{record.validTo ? ` → ${record.validTo}` : ''}</td>
                    <td className="py-2 pr-2">{record.active ? 'Ativa' : 'Inativa'}</td>
                    <td className="py-2 text-right">
                      {record.active ? (
                        <button type="button" onClick={() => deactivateMutation.mutate(record.id)} className="text-[11px] font-semibold text-[var(--text-secondary)]">Desativar</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ModuleCard>
  );
};

export default PlannedDemandPage;
